import React, { memo, useEffect } from 'react'

import { commonGridColDefs } from '@/entities/columnDefinitions/commonColDefs'
import { getGridColDefs } from '@/entities/columnDefinitions/libs/getColDef'
import { fetchUnassignedOrdersByRunsheet } from '@/entities/orders/thunks/fetchUnassignedOrdersByRunsheet'
import { runsheetsSelectors } from '@/entities/schedules'
import { selectBusinessModel } from '@/entities/user'
import { openRightSidebar, setLastSelectedStopId } from '@/entities/userInterface/slices/userInterfaceSlice'
import { setDetailViewedOrder } from '@/entities/userInterface/thunks/setDetailViewedOrder'
import { upsertOneWidgetWindow } from '@/entities/widgetWindows'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { GridRowActionItem } from '@/shared/components/GridTable/models/GridRowActions'
import { TREE_WIDGETS, WidgetTypeWithSuffix } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faFileLines, faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons'
import { Typography } from '@mui/material'
import { GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-premium'

import { useColumnGroupingModel } from './libs/useColumnGroupingModel'
import { useRunsheetUnassignedTableData } from './libs/useRunsheetUnassignedTableData'
import { RunsheetUnassignedTableDropZone } from './RunsheetUnassignedTableDropZone'

export type RunsheetUnassignedTableProps = {
  widgetId: WidgetTypeWithSuffix // Widget Window id
  id: string // Runsheet Scenario Id
}

export const RunsheetUnassignedTable: React.FC<RunsheetUnassignedTableProps> = memo(({ widgetId, id }) => {
  const { rows, isLoading } = useRunsheetUnassignedTableData(id)
  const { columnGroupingModel, initialColumnGroupVisibility } = useColumnGroupingModel()
  const businessModel = useAppSelector(selectBusinessModel)
  const runsheet = useAppSelector(state => runsheetsSelectors.selectById(state, id))
  const runsheetName = runsheet?.name
  const selectedSearchResult = useAppSelector(state => state.userInterface.selectedSearchResult)
  const columns = getGridColDefs({
    gridTypeName: 'runsheetUnassigned',
    additionalColDefs: [commonGridColDefs.geocodeError],
  })

  const dispatch = useAppDispatch()
  const apiRef = useGridApiRef()

  // Update global store with widow title, uiColor, etc.
  useEffect(() => {
    if (runsheetName) {
      dispatch(upsertOneWidgetWindow({
        id: widgetId,
        runsheetId: runsheet?.id,
        isHistorical: Boolean(runsheet?.closedOn),
        runsheetDate: runsheetName,
        isActive: true,
        widgetType: TREE_WIDGETS.RunsheetUnassignedOrders,
        navigations: [
          {
            title: runsheetName || '',
          },
          {
            title: `Unassigned (${rows?.length || 0} Orders)`,
            icon: faFileLines,
          },
        ],
      }))
    }
  }, [dispatch, widgetId, id, runsheetName, runsheet?.id, runsheet?.closedOn, businessModel, rows?.length])

  // Update Selected rows when search result change
  useEffect(() => {
    if (selectedSearchResult && !selectedSearchResult.isPlanning && selectedSearchResult.scheduleId == id &&
      selectedSearchResult.orderId && apiRef?.current?.getRow(selectedSearchResult.orderId)) {
      apiRef.current?.selectRows([selectedSearchResult.orderId], true, true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSearchResult, id])
  
  // Retreieve unassigned orders by runsheet
  useEffect(() => {
    dispatch(fetchUnassignedOrdersByRunsheet({ runsheetId: id }))
  }, [dispatch, id])

  const onGridRowClick = (selectedRowData: GridValidRowModel) => {
    dispatch(setLastSelectedStopId(selectedRowData.id))
  }

  const viewOrderDetailAction: GridRowActionItem = {
    icon: faUpRightFromSquare,
    label: 'View Order',
    onClick: (rowData: GridValidRowModel) => {
      if (rowData.orderId) {
        dispatch(openRightSidebar())
        dispatch(setDetailViewedOrder({ orderId: rowData.orderId }))
      }
    },
  }

  if (!rows) {
    return <Typography variant="body1">Loading...</Typography>
  }

  return (
    <>
      <GridTable
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        checkboxSelection
        isDragDropEnabled
        loading={isLoading}
        disableVirtualization
        pinnedLeftColumns={['geocodeError']}
        onRowClick={onGridRowClick}
        hotAction={viewOrderDetailAction}
        initialColumnGroupVisibility={initialColumnGroupVisibility}
      />
      <RunsheetUnassignedTableDropZone runsheetId={id}/>
    </>

  )
}, (prevProps, nextProps) => {
  return prevProps.id === nextProps.id && prevProps.widgetId === nextProps.widgetId
})
